/**
 * `ShopifyManualInstallPanel` — ADR-0056 §2. The Shopify-specific
 * manual-install surface that replaces the dead-end
 * "RECONNECT TO FINISH INSTALL" reconnect loop on every non-installed
 * terminal of the Shopify ScriptTag auto-install (`scope_denied`,
 * `pending_retry`, `pending`).
 *
 * Why this exists: Shopify's ScriptTag auto-install is structurally
 * blocked on Online Store 2.0 themes (the majority of stores) AND the
 * current OAuth grant deterministically drops `write_script_tags`
 * (the Partners-dashboard scope-set config). Either failure mode
 * leaves the merchant with no way forward — the reconnect button just
 * re-ran the same OAuth flow against the same configured scope set.
 * The manual-install path (paste a `<script>` into the theme's
 * `layout/theme.liquid`) works on BOTH theme generations and is the
 * universal install vector today.
 *
 * Why a separate component (not an extension of `ScriptTagInstallPanel`):
 *   1. The snippet form differs — this panel renders the HASH-form
 *      snippet (`?site=<site_key_hash>` per ADR-0055) because the raw
 *      `site_key` is gone post-onboarding (ADR-0010 §7.2 shown-once).
 *   2. The verify primitive differs — the site is ALREADY ownership-
 *      verified via the OAuth Shopify proof; this panel's "check now"
 *      affordance is FIRST-PING DETECTION, not http-challenge proof.
 *   3. The surrounding instructions are Shopify-specific theme-edit
 *      navigation, not the generic "paste before </body>" framing.
 *
 * The panel renders three regions: (1) the `INSTALL THE EMBED ON
 * SHOPIFY` kicker + 5-step instruction list verified against
 * Shopify's current admin UI, (2) the hash-form snippet with a Copy
 * button, (3) the `I ADDED THE EMBED — CHECK NOW` primary CTA and
 * (optionally) the small secondary "retry auto-install" link.
 */

import { ArrowRight, Check, Copy, RefreshCw } from 'lucide-react';
import { useState } from 'react';

import { buildHashFormScriptSnippet } from '../../lib/install-snippet.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * The CDN origin the manual snippet points at. Single-sourced here so
 * tests can verify the rendered snippet text without parsing env. The
 * production embed CDN is `cdn.sill.so` — the same origin the
 * ADR-0053 auto-installed ScriptTag points at.
 */
export const SHOPIFY_MANUAL_INSTALL_CDN_ORIGIN = 'https://cdn.sill.so';

export interface ShopifyManualInstallPanelProps {
  /**
   * Owner's own site, RLS-scoped — `site_key_hash` MUST be present.
   * The dashboard fetches it via `api.sites.get(site_id)`; the field
   * is optional on the wire (ADR-0056 §3) only because pre-ADR-0056
   * API servers omit it. When the value is missing or empty the
   * panel renders a small "refresh the page" fallback rather than a
   * broken snippet.
   */
  site_key_hash: string | undefined;
  /**
   * Called when the merchant clicks the primary
   * "I ADDED THE EMBED — CHECK NOW" button. The composing context
   * wires this to a fresh re-fetch of the site row so `last_seen_at`
   * can transition the surrounding UI to "live" once a ping arrives.
   * Optional — when omitted the button is hidden.
   */
  onCheckInstalled?: () => void | Promise<void>;
  /**
   * `true` when the panel renders the small secondary
   * "retry auto-install" link. The SiteCard sets this `true` (the
   * merchant might want to retry the OAuth-scoped install after
   * doing the founder-side Partners-dashboard scope fix); the
   * onboarding completion screen sets this `false` (the OAuth
   * callback just attempted the install and failed — retrying
   * immediately is wallet-DoS bait per ADR-0053 §2.5's rate-limit).
   */
  showRetryAutoInstall?: boolean;
  /**
   * Click handler for the secondary "retry auto-install" link. Only
   * meaningful when `showRetryAutoInstall` is true. The composing
   * context wires this to `api.sites.retryShopifyEmbedInstall`.
   */
  onRetryAutoInstall?: () => void | Promise<void>;
  /**
   * When true the primary "check now" button renders a spinner.
   * Wired from the composing context's loading state.
   */
  checking?: boolean;
  /**
   * Optional message rendered below the "check now" CTA after a check
   * round-trip resolves without a fresh `last_seen_at`. The composing
   * context formats the copy ("we haven't seen a ping yet — Shopify
   * caches CSS/JS for up to a few minutes; try again in a moment");
   * the panel just renders it.
   */
  checkResultMessage?: string | undefined;
}

export function ShopifyManualInstallPanel({
  site_key_hash,
  onCheckInstalled,
  showRetryAutoInstall,
  onRetryAutoInstall,
  checking,
  checkResultMessage,
}: ShopifyManualInstallPanelProps): JSX.Element {
  const C = useC();
  const [copied, setCopied] = useState(false);

  const has_hash = typeof site_key_hash === 'string' && site_key_hash.length > 0;
  const snippet = has_hash
    ? buildHashFormScriptSnippet(site_key_hash ?? '', SHOPIFY_MANUAL_INSTALL_CDN_ORIGIN)
    : '';

  const copy = (): void => {
    if (!has_hash) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(snippet);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  // Without a hash there's no snippet to render. Surface the honest
  // one-deploy-window fallback (per ADR-0056 §"Migration / rollout")
  // rather than an empty snippet block.
  if (!has_hash) {
    return (
      <div
        className="p-4"
        style={{
          background: C.amberDim,
          border: `1px solid rgba(255,182,39,0.3)`,
          borderRadius: 3,
        }}
        data-testid="shopify-manual-install-panel-fallback"
      >
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.amberFg,
            letterSpacing: 2,
            marginBottom: 8,
          }}
        >
          INSTALL THE EMBED MANUALLY
        </div>
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            lineHeight: 1.55,
            margin: 0,
          }}
        >
          We couldn&apos;t load your install snippet right now. Refresh the
          page and try again.
        </p>
      </div>
    );
  }

  return (
    <div
      className="p-4"
      style={{
        background: C.amberDim,
        border: `1px solid rgba(255,182,39,0.3)`,
        borderRadius: 3,
      }}
      data-testid="shopify-manual-install-panel"
    >
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.amberFg,
          letterSpacing: 2,
          marginBottom: 6,
        }}
      >
        INSTALL THE EMBED ON SHOPIFY · 1 MINUTE
      </div>
      <p
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.55,
          marginBottom: 12,
          margin: '0 0 12px 0',
        }}
      >
        Sill couldn&apos;t auto-install the embed on this Shopify theme.
        Add it manually — it takes about a minute.
      </p>

      <ol
        style={{
          listStyle: 'decimal inside',
          paddingLeft: 0,
          margin: '0 0 14px 0',
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          lineHeight: 1.65,
        }}
        data-testid="shopify-manual-install-steps"
      >
        <li>
          In your Shopify admin, go to{' '}
          <strong>Online Store → Themes</strong>.
        </li>
        <li>
          On your current theme, click the{' '}
          <span style={{ fontFamily: monoFont, fontSize: 12 }}>···</span>{' '}
          (three dots) menu, then <strong>Edit code</strong>.
        </li>
        <li>
          In the left file tree, open{' '}
          <span style={{ fontFamily: monoFont, fontSize: 12 }}>Layout</span>
          , then click{' '}
          <span style={{ fontFamily: monoFont, fontSize: 12 }}>
            theme.liquid
          </span>
          .
        </li>
        <li>
          Paste the snippet below directly before the closing{' '}
          <span style={{ fontFamily: monoFont, fontSize: 12 }}>
            &lt;/head&gt;
          </span>{' '}
          tag. (If your theme has no{' '}
          <span style={{ fontFamily: monoFont, fontSize: 12 }}>
            &lt;/head&gt;
          </span>
          , paste it before{' '}
          <span style={{ fontFamily: monoFont, fontSize: 12 }}>
            &lt;/body&gt;
          </span>{' '}
          instead.)
        </li>
        <li>
          Click <strong>Save</strong>.
        </li>
      </ol>

      <div
        className="relative p-3 mb-3"
        style={{
          background: C.surface2,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
        }}
        data-testid="shopify-manual-install-snippet"
      >
        <button
          type="button"
          onClick={copy}
          aria-label="Copy snippet"
          className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1"
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            fontFamily: monoFont,
            fontSize: 10,
            color: copied ? C.limeFg : C.muted,
            borderRadius: 2,
            cursor: 'pointer',
          }}
        >
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'COPIED' : 'COPY'}
        </button>
        <pre
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.text,
            lineHeight: 1.7,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            paddingRight: 60,
            margin: 0,
          }}
        >
          {snippet}
        </pre>
      </div>

      {onCheckInstalled !== undefined ? (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-2">
          <button
            type="button"
            onClick={() => void onCheckInstalled()}
            disabled={checking === true}
            className="flex items-center justify-center gap-2 px-4 py-2"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              border: 'none',
              opacity: checking === true ? 0.6 : 1,
              cursor: checking === true ? 'wait' : 'pointer',
            }}
            data-testid="shopify-manual-install-check-now"
          >
            {checking === true ? (
              <>
                <RefreshCw
                  size={13}
                  style={{ animation: 'spin 1.4s linear infinite' }}
                />
                Checking…
              </>
            ) : (
              <>
                I added the embed — check now <ArrowRight size={13} />
              </>
            )}
          </button>
          {checkResultMessage !== undefined && checkResultMessage.length > 0 ? (
            <span
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.muted,
                lineHeight: 1.5,
                maxWidth: 420,
              }}
              data-testid="shopify-manual-install-check-result"
            >
              {checkResultMessage}
            </span>
          ) : null}
        </div>
      ) : null}

      <p
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
          margin: '8px 0 0 0',
        }}
      >
        On theme switches, paste this on the new theme too. The embed
        installs at the storefront level; switching themes resets the
        storefront&apos;s rendered HTML.
      </p>

      {showRetryAutoInstall === true && onRetryAutoInstall !== undefined ? (
        <button
          type="button"
          onClick={() => void onRetryAutoInstall()}
          className="mt-3"
          style={{
            background: 'transparent',
            border: 0,
            padding: 0,
            fontFamily: sansFont,
            fontSize: 11,
            color: C.muted,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          data-testid="shopify-manual-install-retry-auto"
        >
          Or retry auto-install →
        </button>
      ) : null}
    </div>
  );
}
